/**
 * Streamed export audio: renders the mix window by window (see `audio-windows.ts`).
 *
 * Memory is bounded by the window length: each window decodes only the source spans it plays
 * (plus a short pre-roll that absorbs decoder warm-up after seeking, and a tail for resampler
 * interpolation), mixes them with the same `scheduleAudio` graph as the preview in a small
 * OfflineAudioContext, and returns the window's PCM. Decoders are released after the last window
 * that needs them.
 */
import type { AudioSegment, Project } from '@ve/core';
import { ALL_FORMATS, AudioBufferSink, Input, type InputAudioTrack } from 'mediabunny';
import { decodeAudio, NoAudioError } from '../audio/decode-audio.ts';
import { scheduleAudio } from '../audio/schedule.ts';
import { sourceFor } from '../media/input.ts';
import type { MediaInput, MediaResolver } from '../types.ts';
import { lastWindowByMedia, partsForWindow, planAudioWindows, type AudioWindow } from './audio-windows.ts';

/** Decoded before the needed span and discarded: covers codec warm-up (AAC/Opus/MP3 bit reservoir). */
const PREROLL_SECONDS = 0.25;
/** Decoded after the needed span so resampling never runs off the end of the buffer. */
const TAIL_SECONDS = 0.05;
const MAX_CHANNELS = 8;
export const DEFAULT_AUDIO_WINDOW_SECONDS = 10;

type SourceEntry =
  | { kind: 'stream'; input: Input; track: InputAudioTrack }
  /** mediabunny cannot decode it: decoded once in full (fallback path, rare). */
  | { kind: 'full'; buffer: AudioBuffer }
  | { kind: 'silent' };

interface DecodedSpan {
  buffer: AudioBuffer;
  /** Media time (seconds) of the buffer's first sample. */
  t0: number;
}

/** Opens media audio lazily and decodes arbitrary source spans. */
class SourceAudio {
  private entries = new Map<string, Promise<SourceEntry>>();
  private fallbackContext: OfflineAudioContext | null = null;

  constructor(
    private readonly project: Project,
    private readonly resolveMedia: MediaResolver,
  ) {}

  private inputOf(mediaId: string): MediaInput {
    const sources = this.resolveMedia(mediaId);
    const input = sources ? (sources.audio ?? sources.master) : undefined;
    if (!input) throw new Error(`Media "${this.project.media[mediaId]?.name ?? mediaId}" is not available`);
    return input;
  }

  private open(mediaId: string): Promise<SourceEntry> {
    let entry = this.entries.get(mediaId);
    if (!entry) {
      entry = this.openEntry(mediaId);
      this.entries.set(mediaId, entry);
    }
    return entry;
  }

  private async openEntry(mediaId: string): Promise<SourceEntry> {
    const media = this.inputOf(mediaId);
    const input = new Input({ source: sourceFor(media), formats: ALL_FORMATS });
    let track: InputAudioTrack | null | undefined;
    try {
      track = await input.getPrimaryAudioTrack();
    } catch {
      track = undefined; // unreadable container → full-decode fallback
    }
    if (track === null) {
      input.dispose();
      return { kind: 'silent' };
    }
    if (track && (await track.canDecode())) return { kind: 'stream', input, track };
    input.dispose();
    this.fallbackContext ??= new OfflineAudioContext({ numberOfChannels: 1, length: 1, sampleRate: 48_000 });
    try {
      return { kind: 'full', buffer: await decodeAudio(media, this.fallbackContext) };
    } catch (err) {
      if (err instanceof NoAudioError) return { kind: 'silent' };
      throw err;
    }
  }

  /** Decodes `[from, to)` (media seconds) of a media item's audio, or null if it has none. */
  async span(mediaId: string, from: number, to: number): Promise<DecodedSpan | null> {
    const entry = await this.open(mediaId);
    if (entry.kind === 'silent') return null;
    if (entry.kind === 'full') return { buffer: entry.buffer, t0: 0 };

    const chunks: Array<{ buffer: AudioBuffer; timestamp: number }> = [];
    let t0 = Infinity;
    let tEnd = -Infinity;
    let channels = 1;
    const sink = new AudioBufferSink(entry.track);
    for await (const { buffer, timestamp } of sink.buffers(
      Math.max(0, from - PREROLL_SECONDS),
      to + TAIL_SECONDS,
    )) {
      chunks.push({ buffer, timestamp });
      t0 = Math.min(t0, timestamp);
      tEnd = Math.max(tEnd, timestamp + buffer.duration);
      channels = Math.max(channels, buffer.numberOfChannels);
    }
    if (chunks.length === 0) return null;
    const sampleRate = chunks[0]!.buffer.sampleRate;
    const out = new AudioBuffer({
      length: Math.max(1, Math.round((tEnd - t0) * sampleRate)),
      numberOfChannels: Math.min(MAX_CHANNELS, channels),
      sampleRate,
    });
    for (const chunk of chunks) {
      const offset = Math.round((chunk.timestamp - t0) * sampleRate);
      const n = Math.min(chunk.buffer.length, out.length - offset);
      if (n <= 0) continue;
      for (let c = 0; c < out.numberOfChannels; c++) {
        const src = chunk.buffer.getChannelData(Math.min(c, chunk.buffer.numberOfChannels - 1));
        out.copyToChannel(src.subarray(0, n), c, offset);
      }
    }
    return { buffer: out, t0 };
  }

  async release(mediaId: string): Promise<void> {
    const entry = this.entries.get(mediaId);
    this.entries.delete(mediaId);
    const resolved = await entry?.catch(() => null);
    if (resolved?.kind === 'stream') resolved.input.dispose();
  }

  async dispose(): Promise<void> {
    await Promise.all([...this.entries.keys()].map((id) => this.release(id)));
  }
}

export class WindowedAudioMixer {
  readonly windows: AudioWindow[];
  private readonly lastUse: Map<string, number>;
  private readonly sources: SourceAudio;

  constructor(
    project: Project,
    private readonly segments: readonly AudioSegment[],
    resolveMedia: MediaResolver,
    startSec: number,
    endSec: number,
    private readonly sampleRate: number,
    windowSeconds = DEFAULT_AUDIO_WINDOW_SECONDS,
  ) {
    this.windows = planAudioWindows(startSec, endSec, sampleRate, windowSeconds);
    this.lastUse = lastWindowByMedia(segments, this.windows);
    this.sources = new SourceAudio(project, resolveMedia);
  }

  /** Renders one window of the stereo mix. Windows may be rendered in any order. */
  async render(win: AudioWindow): Promise<AudioBuffer> {
    const ctx = new OfflineAudioContext({
      numberOfChannels: 2,
      length: win.endSample - win.startSample,
      sampleRate: this.sampleRate,
    });
    const parts = partsForWindow(this.segments, win);
    const spans = await Promise.all(
      parts.map((p) => this.sources.span(p.segment.mediaId, p.sourceFrom, p.sourceTo)),
    );
    parts.forEach((part, i) => {
      const span = spans[i];
      if (!span) return; // no audio track: silent
      // Re-express the segment against the decoded span (whose sample 0 is media time `t0`).
      const shifted: AudioSegment = { ...part.segment, sourceStart: part.segment.sourceStart - span.t0 };
      scheduleAudio(ctx, ctx.destination, [shifted], () => span.buffer, {
        timelineTime: win.from,
        contextTime: 0,
        endTime: win.to,
      });
    });
    const rendered = await ctx.startRendering();
    for (const [mediaId, last] of this.lastUse) {
      if (last <= win.index) {
        this.lastUse.delete(mediaId);
        await this.sources.release(mediaId);
      }
    }
    return rendered;
  }

  dispose(): Promise<void> {
    return this.sources.dispose();
  }
}
